/*
Decides which rounds load cache-first: render from the IndexedDB-cached
aggregate (when it is within CACHE_FIRST_MAX_ACTION_GAP of the server) and let
the socket stream the gap, instead of re-downloading the multi-MB aggregate on
every visit.

A round qualifies when it is on the static allow-list (a seed for first
visits — the list is baked into shipped bundles), OR when this client has
observed the round's downloaded payload to be at least
cacheFirstMinAggregateChars(). Observations are per (round, effective viewer):
the server anonymizes the payload per viewer, so sizes are not comparable
across viewers.

The threshold exists because cache-first trades the download for one extra
serial round trip (getRoundBootstrap): measured a wash below ~1.5M chars,
a win that grows with payload size above it.
*/

// The small, explicit set of rounds currently under funder review. These are
// restored cache-first in the browser (even on a first visit, before any
// learned-size observation exists) and comprehensively prewarmed + pinned on
// the server. Add the other review round id(s) here or via env.
export const DEFAULT_CACHE_FIRST_ROUND_IDS = ["r-8PQ8le63"] as const;

function parseEnvRoundIds(): string[] {
  const raw = import.meta.env?.VITE_CACHE_FIRST_ROUND_IDS;

  if (typeof raw !== "string") {
    return [];
  }

  return raw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

const cacheFirstRoundIds = new Set<string>([
  ...DEFAULT_CACHE_FIRST_ROUND_IDS,
  ...parseEnvRoundIds(),
]);

// Seams are live in non-production builds and in VITE_E2E_TEST_SEAMS prod
// builds (the e2e prod-build mode); real deploys compile them away.
function e2eSeamsEnabled(): boolean {
  return (
    import.meta.env?.PROD !== true ||
    import.meta.env?.VITE_E2E_TEST_SEAMS === "true"
  );
}

// Adds rounds (comma-separated localStorage flag) to the allow-list per test:
// the build-time list can't be changed by the e2e suite.
function e2eForcedCacheFirstRoundIds(): string[] {
  if (!e2eSeamsEnabled()) {
    return [];
  }

  if (typeof localStorage === "undefined") {
    return [];
  }

  const raw = localStorage.getItem("e2eForceCacheFirstRoundIds");

  if (raw == null) {
    return [];
  }

  return raw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

// In JSON string chars (≈ bytes). Calibration: the fixture round is ~1.55M
// chars (measured as a wash), the big production round ~2.1M and growing; a
// borderline round pays at most the one extra bootstrap round trip (~50ms).
// Override via VITE_CACHE_FIRST_MIN_AGGREGATE_CHARS.
const DEFAULT_MIN_AGGREGATE_CHARS = 2_000_000;

export function cacheFirstMinAggregateChars(): number {
  if (e2eSeamsEnabled() && typeof localStorage !== "undefined") {
    // e2e seam: lets tests lower the threshold below the fixture round's size.
    const seam = Number(localStorage.getItem("e2eCacheFirstMinAggregateChars"));

    if (Number.isFinite(seam) && seam > 0) {
      return seam;
    }
  }

  const raw = import.meta.env?.VITE_CACHE_FIRST_MIN_AGGREGATE_CHARS;
  const parsed = typeof raw === "string" ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_MIN_AGGREGATE_CHARS;
}

// localStorage map "roundId:viewer" -> serialized chars of the last download.
// Key order is insertion order, which doubles as the eviction order.
const OBSERVED_SIZES_STORAGE_KEY = "cacheFirstObservedAggregateChars";
// Guards against unbounded growth on a long-lived browser profile.
const MAX_OBSERVED_ENTRIES = 50;

// Slot for the non-impersonating user, whose id isn't yet known when the
// decision runs. Sign-out clears the map, so successive logins on a shared
// device don't share this slot.
const SELF_SCOPE = "self";

function observedSizeKey(
  roundId: string,
  impersonatedUserId: string | null,
): string {
  return `${roundId}:${impersonatedUserId ?? SELF_SCOPE}`;
}

function readObservedSizes(): { [viewerScopedKey: string]: number } {
  if (typeof localStorage === "undefined") {
    return {};
  }

  try {
    const raw = localStorage.getItem(OBSERVED_SIZES_STORAGE_KEY);

    if (raw == null) {
      return {};
    }

    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== "object" || parsed == null || Array.isArray(parsed)) {
      return {};
    }

    const sizes: { [viewerScopedKey: string]: number } = {};

    for (const [key, chars] of Object.entries(parsed)) {
      if (typeof chars === "number" && Number.isFinite(chars) && chars >= 0) {
        sizes[key] = chars;
      }
    }

    return sizes;
  } catch {
    // Corrupt JSON: treat as empty; the next record overwrites it.
    return {};
  }
}

/**
 * Record a downloaded aggregate's serialized size for the next visit's
 * decision. `impersonatedUserId` must be the impersonation in effect when the
 * request was built (it determines whose payload the server sent).
 * Best-effort: storage failures never break the load.
 */
export function recordObservedAggregateSize(
  roundId: string,
  impersonatedUserId: string | null,
  serializedChars: number,
): void {
  if (typeof localStorage === "undefined") {
    return;
  }

  if (!Number.isFinite(serializedChars) || serializedChars < 0) {
    return;
  }

  try {
    const sizes = readObservedSizes();
    const key = observedSizeKey(roundId, impersonatedUserId);
    // Delete-then-set moves the entry to the back of the eviction order.
    delete sizes[key];
    sizes[key] = serializedChars;
    const keys = Object.keys(sizes);

    for (const oldest of keys.slice(
      0,
      Math.max(0, keys.length - MAX_OBSERVED_ENTRIES),
    )) {
      delete sizes[oldest];
    }

    localStorage.setItem(OBSERVED_SIZES_STORAGE_KEY, JSON.stringify(sizes));
  } catch {
    // Best-effort only.
  }
}

/**
 * Dev tooling (SP-2637): drop the learned-size records for ONE round, every
 * viewer scope, so the next open decides cache-first from scratch — part of
 * the "Forget this round on this device" wipe.
 */
export function removeObservedAggregateSizesForRound(roundId: string): void {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    const sizes = readObservedSizes();
    let changed = false;

    for (const key of Object.keys(sizes)) {
      if (key.startsWith(`${roundId}:`)) {
        delete sizes[key];
        changed = true;
      }
    }

    if (changed) {
      localStorage.setItem(OBSERVED_SIZES_STORAGE_KEY, JSON.stringify(sizes));
    }
  } catch {
    // Best-effort only.
  }
}

/** Called on sign-out / account switch: learned sizes are identity-scoped. */
export function clearObservedAggregateSizes(): void {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(OBSERVED_SIZES_STORAGE_KEY);
  } catch {
    // Best-effort only.
  }
}

function isLearnedCacheFirstRound(
  roundId: string,
  impersonatedUserId: string | null,
): boolean {
  const observed =
    readObservedSizes()[observedSizeKey(roundId, impersonatedUserId)];
  return observed != null && observed >= cacheFirstMinAggregateChars();
}

// Server callers pass `impersonatedUserId: null` — learned sizes live in the
// browser's localStorage, so on the server this reduces to the static list.
export function isCacheFirstEnabled(
  roundId: string,
  impersonatedUserId: string | null,
): boolean {
  return (
    cacheFirstRoundIds.has(roundId) ||
    isLearnedCacheFirstRound(roundId, impersonatedUserId) ||
    e2eForcedCacheFirstRoundIds().includes(roundId)
  );
}

// The full allow-list, for server-side pre-warming (the corpus response cache
// warms these rounds at boot — see prewarmCorpusResponses.ts). Works under Bun
// too: import.meta.env resolves to process.env there.
export function cacheFirstRoundIdList(): string[] {
  return [...cacheFirstRoundIds];
}

// How many actions a cached aggregate may be behind the server before we
// download fresh instead of catching up over the socket. Catching up a few
// hundred actions is fast; thousands is not (the big rounds total ~18k+
// actions), so this is the "too far behind -> refetch" threshold.
export const CACHE_FIRST_MAX_ACTION_GAP = 250;
